/* eslint-disable @typescript-eslint/no-explicit-any, max-lines */
import { IAdFoxBannerOnLoadData } from '@cian/adfox-component';
import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import includes from 'lodash/includes';

import { setCookie } from '@cian/utils/lib/browser/cookie';
import { isLoaderContext, parseAdfoxParams } from '../../utils/adfox';

import * as Analytics from '../../utils/analytics_helper';
import { matchQueryParams } from './query_params_matcher';
import { renderFilters } from './search_config';
import { useDefaultImageAnimationEnabled } from './hooks';
import { IApplicationState } from '../../types/redux';
import { shouldNewFiltersShow } from '../../utils/filters';
import { getSearchUrl, IJsonQueryTerms, getMapUrl } from '../../reducers/modules/search/search';
import { SearchFilters } from '../../components/search_filters/search_filters';
import { SearchBlock } from '../../components/SearchBlock';
import { getFiltersConfiguration } from '../../utils/getFiltersConfiguration';
import { TypeFilter } from '../filters/type_filter';
import { BuildingTypeFilter } from '../filters/building_type_filter';
import { useApplicationContext } from '../../utils/applicationContext';
import { FiltersBackground } from '../../components/FiltersBackground';
import { HeroContainer } from '../HeroContainer';
import { selectPageSection } from '../../selectors/pageSection';
import { ESectionType } from '../../types/sectionType';
import { Marquee } from '../../components/Marquee';
import { AdfoxNew } from '../../components/adfox/adfoxNew';
import { SearchBlockAdLayout } from '../../components/SearchBlockAdLayout';
import { ADFOX_MEDIA_BANNER_LABEL, ADFOX_NEW_MEDIA_CONSTANTS } from '../../components/adfox/constants';
import { useSearchStringRedesignEnabled } from '../../hooks/useSearchStringRedesignEnabled';
import { selectSeoData } from '../../selectors/seoData';

type TAdFoxContext =
  | {
      newBuildingId: string;
      isApartment: string;
      bgImageUrl: string;
      note: string;
      label?: string;
      analyticsBannerId: string;
    }
  | IAdFoxBannerOnLoadData;

interface ISearchUrl {
  searchUrl: string;
  queryStringOrPathName: string;
}

const MSC_REGION_IDS = [-1, 1, 4593];
const SPB_REGION_IDS = [-2, 2, 4588];
const TOP_REGIONS = [...MSC_REGION_IDS, ...SPB_REGION_IDS];

function replaceSearchPath(type: string, url: string, isNewBuildingsSection: boolean) {
  const searchUrlRe = isNewBuildingsSection ? /\/newobjects\/list\// : /\/cat\.php/;
  const filtersFormUrl = isNewBuildingsSection ? '/newobjects/search/' : '/nd/search/';

  return url.replace(searchUrlRe, filtersFormUrl);
}

export const SearchBlockContainer: React.FC = () => {
  const { telemetry } = useApplicationContext();

  const dispatch = useDispatch();
  const section = useSelector(selectPageSection);
  const type = useSelector((state: IApplicationState) => state.type);
  const buildingType = useSelector((state: IApplicationState) => state.buildingType);
  const square = useSelector((state: IApplicationState) => state.square);
  const rooms = useSelector((state: IApplicationState) => state.rooms);
  const price = useSelector((state: IApplicationState) => state.price);
  const userGeo = useSelector((state: IApplicationState) => state.userGeo);
  const year = useSelector((state: IApplicationState) => state.year);
  const suburbanOfferFilter = useSelector((state: IApplicationState) => state.suburbanOfferFilter);
  const user = useSelector((state: IApplicationState) => state.user);
  const { abGroup } = useSelector((state: IApplicationState) => state.config);
  const defaultBackground = useSelector((state: IApplicationState) => state.defaultBackground);
  const seoData = useSelector(selectSeoData);

  const { actualBuildingType, buildingTypes, searchConfig } = React.useMemo(
    () => getFiltersConfiguration(buildingType, section),
    [buildingType, section],
  );

  const timer = React.useRef<number | undefined>();

  const [backgroundShown, setBackgroundShown] = React.useState(false);
  const [backgroundImageUrl, setBackgroundImageUrl] = React.useState<string | undefined>(undefined);
  const [mediaNote, setMediaNote] = React.useState<string | undefined>(undefined);
  const [label, setLabel] = React.useState<string | undefined>(); // for disable eslint warning about unused variable

  const isNewFilters = shouldNewFiltersShow({ section });
  const isRedesignEnabled = useSearchStringRedesignEnabled();
  const isNewBuildingsSection = section === ESectionType.NewBuildings;
  const isDailyRent = section === ESectionType.DailyRent;

  const isDefaultImageAnimationEnabled = useDefaultImageAnimationEnabled(section);

  React.useEffect(() => {
    timer.current = window.setTimeout(() => {
      setBackgroundShown(true);
      setBackgroundImageUrl(undefined);
    }, 1500);

    return () => {
      window.clearTimeout(timer.current);
    };
  }, []);

  const onStubAdfox = React.useCallback(() => {
    window.clearTimeout(timer.current);
    setBackgroundShown(true);
  }, []);

  const onLoadAdfox = React.useCallback(
    (context: TAdFoxContext) => {
      const params = isLoaderContext(context) ? parseAdfoxParams(context) : context;

      if (!params) {
        return;
      }

      window.clearTimeout(timer.current);

      setBackgroundShown(true);
      setMediaNote(params.note);
      setLabel(params.label);
      setBackgroundImageUrl(params.bgImageUrl);

      telemetry.increment('filters.adFox.show');
    },
    [telemetry],
  );

  const submitSearch = React.useCallback(
    (e: React.SyntheticEvent<HTMLElement>) => {
      e.preventDefault();

      const params = matchQueryParams(
        type,
        actualBuildingType,
        square,
        rooms,
        price,
        userGeo,
        year,
        isNewBuildingsSection,
        suburbanOfferFilter,
      );

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (dispatch(getSearchUrl(params)) as any).then((url: ISearchUrl) => {
        let searchUrl = url.searchUrl;
        // istanbul ignore next
        if (
          (params.region && includes(TOP_REGIONS, (params.region as IJsonQueryTerms<number>).value[0])) ||
          isNewBuildingsSection
        ) {
          searchUrl = replaceSearchPath(params._type, searchUrl, isNewBuildingsSection);
        }

        Analytics.trackSearchEvent('button_search', section, url.queryStringOrPathName, params, user, abGroup);

        /**
         * Ставим сессионую гео-куку на основе полученных данных от гео фильтра
         */
        const { regionId, mainTownId } = userGeo.userGeoDefinitionWithGeoFilter;

        if (regionId) {
          setCookie('session_region_id', String(regionId), { secure: false });
        }

        if (mainTownId) {
          setCookie('session_main_town_region_id', String(mainTownId), { secure: false });
        }

        window.location.href = searchUrl;
      });
    },
    [
      abGroup,
      actualBuildingType,
      dispatch,
      isNewBuildingsSection,
      price,
      rooms,
      section,
      square,
      suburbanOfferFilter,
      type,
      user,
      userGeo,
      year,
    ],
  );

  const submitMapSearch = React.useCallback(
    (e: React.SyntheticEvent<HTMLElement>) => {
      e.preventDefault();

      const params = matchQueryParams(
        type,
        actualBuildingType,
        square,
        rooms,
        price,
        userGeo,
        year,
        isNewBuildingsSection,
        suburbanOfferFilter,
      );

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (dispatch(getMapUrl(params)) as any).then((url: string) => {
        Analytics.trackSearchEvent('search_on_map', section, url, params, user, abGroup);

        Analytics.clickSearchOnMapButtonOnFilter(section, url);

        window.location.href = url;
      });
    },
    [
      abGroup,
      actualBuildingType,
      dispatch,
      isNewBuildingsSection,
      price,
      rooms,
      section,
      square,
      suburbanOfferFilter,
      type,
      user,
      userGeo,
      year,
    ],
  );

  React.useEffect(() => {
    /* на посуточной не нужна реклама */
    if (isDailyRent) {
      if (onStubAdfox) {
        onStubAdfox();
      }
    }
  }, [onStubAdfox, isDailyRent]);

  const adBanner = React.useMemo(() => {
    if (isDailyRent) {
      return null;
    }

    return (
      <SearchBlockAdLayout>
        <AdfoxNew
          postViewAnalyticsData={{
            place: 'filters_background',
            place__DEPRECATED: `home_${section}`,
            name__DEPRECATED: ADFOX_MEDIA_BANNER_LABEL,
          }}
          constants={ADFOX_NEW_MEDIA_CONSTANTS}
          onLoad={onLoadAdfox}
          onStub={onStubAdfox}
        />
        <Marquee mediaNote={mediaNote} isRedesignEnabled={isRedesignEnabled.isEnabled} />
      </SearchBlockAdLayout>
    );
  }, [isDailyRent, mediaNote, onLoadAdfox, onStubAdfox, section]);

  return (
    <SearchBlock
      section={section}
      isRedesignEnabled={isRedesignEnabled.isEnabled}
      isNewFilters={isNewFilters}
      label={label}
      adBanner={adBanner}
      backgroundNode={
        backgroundShown && (
          <FiltersBackground
            section={section}
            imageUrl={backgroundImageUrl}
            defaultImageUrl={defaultBackground}
            isDefaultImageAnimationEnabled={isDefaultImageAnimationEnabled}
          />
        )
      }
    >
      {isNewFilters ? (
        <HeroContainer />
      ) : (
        <SearchFilters
          isDobroshrift={false}
          title={seoData?.h1 || ''}
          onSubmit={submitSearch}
          onMapSubmit={submitMapSearch}
        >
          {!isNewBuildingsSection && (
            <>
              <TypeFilter buildingTypes={buildingTypes} searchConfig={searchConfig} />
              <BuildingTypeFilter
                buildingType={buildingType.buildingType}
                buildingTypes={buildingTypes}
                searchConfig={searchConfig}
              />
            </>
          )}
          {renderFilters(type.searchType, buildingType.buildingType, isNewBuildingsSection, searchConfig)}
        </SearchFilters>
      )}
    </SearchBlock>
  );
};
